import React from 'react';
import { Conversation, ConversationAnalysis } from '../services/api';
import { ANALYSIS_ESTIMATED_SECONDS } from '../config/analysis';

interface AnalysisModalProps {
  conversation: Conversation;
  analysis: ConversationAnalysis | undefined;
  isLoading?: boolean;
  onClose: () => void;
}

const AnalysisModal: React.FC<AnalysisModalProps> = ({
  conversation,
  analysis,
  isLoading = false,
  onClose,
}) => {
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}>
        <div style={{
          backgroundColor: '#000000',
          border: '2px solid #ffffff',
          borderRadius: 0,
          padding: '2rem',
          maxWidth: '28rem',
          margin: '1rem',
          textAlign: 'center'
        }}>
          <div className="flex justify-center mb-4">
            <div
              className="animate-spin rounded-full h-12 w-12"
              style={{
                borderWidth: '2px',
                borderStyle: 'solid',
                borderColor: '#ffffff transparent transparent transparent',
              }}
            />
          </div>
          <h3
            style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              marginBottom: '0.5rem',
              color: '#ffffff',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Processing Analysis
          </h3>
          <p
            style={{
              color: '#ffffff',
              marginBottom: '1.5rem',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Analyzing your conversation with OpenAI. This may take up to {ANALYSIS_ESTIMATED_SECONDS} seconds.
          </p>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '1px solid #ffffff',
              color: '#ffffff',
              padding: '0.6rem 1.5rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'Inter', sans-serif",
              borderRadius: 0,
              textTransform: 'uppercase',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.color = '#000000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#ffffff';
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return null;
  }

  const getScoreColor = (score: number) => {
    if (score >= 85) return { color: '#2ecc71', bg: 'rgba(46, 204, 113, 0.15)' };
    if (score >= 70) return { color: '#3498db', bg: 'rgba(52, 152, 219, 0.15)' };
    if (score >= 50) return { color: '#ffffff', bg: 'rgba(222, 184, 135, 0.15)' };
    if (score >= 35) return { color: '#e67e22', bg: 'rgba(230, 126, 34, 0.15)' };
    return { color: '#e74c3c', bg: 'rgba(231, 76, 60, 0.15)' };
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'EXCEPTIONAL';
    if (score >= 80) return 'EXCELLENT';
    if (score >= 70) return 'GOOD';
    if (score >= 50) return 'FAIR';
    if (score >= 35) return 'NEEDS WORK';
    return 'POOR';
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.92)' }}>
      <div style={{
        backgroundColor: '#000000',
        border: '2px solid #ffffff',
        borderRadius: 0,
        maxWidth: '64rem',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          backgroundColor: '#000000',
          borderBottom: '1px solid rgba(222, 184, 135, 0.3)',
          padding: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 10
        }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#ffffff',
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}>
            Analysis: {conversation.title}
          </h2>
          <button
            onClick={onClose}
            style={{
              color: '#ffffff',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#ffffff'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6" style={{ padding: '2rem' }}>
          {/* Overall Score */}
          <div style={{
            backgroundColor: 'rgba(222, 184, 135, 0.08)',
            border: '1px solid rgba(222, 184, 135, 0.3)',
            borderRadius: 0,
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '1rem 2rem',
              border: `2px solid ${getScoreColor(analysis.overall_score).color}`,
              fontSize: '2.5rem',
              fontWeight: 800,
              color: getScoreColor(analysis.overall_score).color,
              backgroundColor: getScoreColor(analysis.overall_score).bg,
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em'
            }}>
              {analysis.overall_score}/100
            </div>
            <p style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#ffffff',
              marginTop: '1rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em'
            }}>
              {getScoreLabel(analysis.overall_score)}
            </p>
            <p style={{
              color: '#ffffff',
              fontFamily: "'Inter', sans-serif",
              fontSize: '0.875rem',
              marginTop: '0.5rem'
            }}>
              Overall Prompting Score
            </p>
          </div>

          {/* Detailed Scores */}
          <div>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#ffffff',
              marginBottom: '1rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em'
            }}>
              Detailed Criteria Scores
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { score: analysis.communication_clarity_score, name: 'Communication Clarity', desc: 'How well you communicated what you want' },
                { score: analysis.efficiency_score, name: 'Efficiency', desc: 'How concise and direct your prompts were' },
                { score: analysis.practical_impact_score, name: 'Practical Impact', desc: 'How likely this is to get useful results' }
              ].map((item, idx) => (
                <div key={idx} style={{
                  backgroundColor: 'rgba(245, 245, 220, 0.05)',
                  border: '1px solid rgba(245, 245, 220, 0.2)',
                  borderRadius: 0,
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.5rem 1rem',
                    borderRadius: 0,
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: getScoreColor(item.score).color,
                    backgroundColor: getScoreColor(item.score).bg,
                    border: `1px solid ${getScoreColor(item.score).color}`,
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {item.score}
                  </div>
                  <p style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#ffffff',
                    marginTop: '0.75rem',
                    fontFamily: "'Inter', sans-serif",
                    letterSpacing: '0.02em'
                  }}>
                    {item.name}
                  </p>
                  <p style={{
                    fontSize: '0.75rem',
                    color: '#ffffff',
                    marginTop: '0.25rem',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Safety Flags */}
          {analysis.safety_flags && analysis.safety_flags.length > 0 && (
            <div style={{
              backgroundColor: 'rgba(231, 76, 60, 0.1)',
              border: '2px solid #e74c3c',
              borderRadius: 0,
              padding: '1rem'
            }}>
              <h3 style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: '#e74c3c',
                marginBottom: '0.75rem',
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.05em'
              }}>
                ⚠️ Safety Concerns
              </h3>
              <ul className="space-y-2">
                {analysis.safety_flags.map((flag, index) => (
                  <li key={index} className="flex items-start">
                    <span style={{ color: '#e74c3c', marginRight: '0.5rem' }}>⚠</span>
                    <span style={{ color: '#ffffff', fontFamily: "'Inter', sans-serif", fontSize: '0.875rem' }}>{flag}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Enhanced Prompts */}
          {analysis.enhanced_prompts && analysis.enhanced_prompts.length > 0 && (
            <div>
              <h3 style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: '#ffffff',
                marginBottom: '0.75rem',
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.05em'
              }}>
                Enhanced Prompts
              </h3>
              <div className="space-y-3">
                {analysis.enhanced_prompts.map((prompt, index) => (
                  <div key={index} style={{
                    backgroundColor: 'rgba(52, 152, 219, 0.1)',
                    border: '1px solid rgba(52, 152, 219, 0.3)',
                    borderLeft: '3px solid #3498db',
                    borderRadius: 0,
                    padding: '1rem'
                  }}>
                    <p style={{
                      color: '#ffffff',
                      whiteSpace: 'pre-wrap',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.875rem',
                      lineHeight: '1.6'
                    }}>
                      {prompt}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feedback Points */}
          {analysis.feedback_points && analysis.feedback_points.length > 0 && (
            <div>
              <h3 style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: '#ffffff',
                marginBottom: '0.75rem',
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.05em'
              }}>
                Key Feedback Points
              </h3>
              <ul className="space-y-2">
                {analysis.feedback_points.map((point, index) => (
                  <li key={index} className="flex items-start">
                    <span style={{ color: '#ffffff', marginRight: '0.5rem', fontSize: '1.25rem' }}>•</span>
                    <span style={{
                      color: '#ffffff',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.875rem',
                      lineHeight: '1.6'
                    }}>
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Strengths and Weaknesses */}
          <div className="grid md:grid-cols-2 gap-6">
            {analysis.strengths && analysis.strengths.length > 0 && (
              <div>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#2ecc71',
                  marginBottom: '0.75rem',
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: '0.05em'
                }}>
                  Strengths
                </h3>
                <ul className="space-y-2">
                  {analysis.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start">
                      <span style={{ color: '#2ecc71', marginRight: '0.5rem' }}>✓</span>
                      <span style={{
                        color: '#ffffff',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '0.875rem',
                        lineHeight: '1.6'
                      }}>
                        {strength}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.weaknesses && analysis.weaknesses.length > 0 && (
              <div>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#e67e22',
                  marginBottom: '0.75rem',
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: '0.05em'
                }}>
                  Areas for Improvement
                </h3>
                <ul className="space-y-2">
                  {analysis.weaknesses.map((weakness, index) => (
                    <li key={index} className="flex items-start">
                      <span style={{ color: '#e67e22', marginRight: '0.5rem' }}>⚠</span>
                      <span style={{
                        color: '#ffffff',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '0.875rem',
                        lineHeight: '1.6'
                      }}>
                        {weakness}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Suggestions */}
          {analysis.suggestions && analysis.suggestions.length > 0 && (
            <div>
              <h3 style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: '#ffffff',
                marginBottom: '0.75rem',
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.05em'
              }}>
                Suggestions for Better Prompting
              </h3>
              <ul className="space-y-2">
                {analysis.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start">
                    <span style={{ marginRight: '0.5rem' }}>💡</span>
                    <span style={{
                      color: '#ffffff',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.875rem',
                      lineHeight: '1.6'
                    }}>
                      {suggestion}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Original Conversation */}
          <div>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#ffffff',
              marginBottom: '0.75rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em'
            }}>
              Original Conversation
            </h3>
            <div style={{
              backgroundColor: 'rgba(10, 10, 10, 0.5)',
              border: '1px solid rgba(245, 245, 220, 0.1)',
              borderRadius: 0,
              padding: '1rem',
              maxHeight: '24rem',
              overflowY: 'auto'
            }}>
              {conversation.messages.map((message, index) => (
                <div key={index} style={{
                  marginBottom: '0.75rem',
                  padding: '0.875rem',
                  borderRadius: 0,
                  backgroundColor: message.role === 'user' 
                    ? 'rgba(222, 184, 135, 0.1)' 
                    : message.role === 'assistant'
                    ? 'rgba(245, 245, 220, 0.05)'
                    : 'rgba(230, 126, 34, 0.1)',
                  border: message.role === 'user'
                    ? '1px solid rgba(222, 184, 135, 0.3)'
                    : message.role === 'assistant'
                    ? '1px solid rgba(245, 245, 220, 0.1)'
                    : '1px solid rgba(230, 126, 34, 0.3)',
                  marginLeft: message.role === 'user' ? '2rem' : '0',
                  marginRight: message.role === 'assistant' ? '2rem' : '0'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: message.role === 'user' ? '#ffffff' : '#ffffff',
                    marginBottom: '0.25rem',
                    fontFamily: "'Inter', sans-serif",
                    letterSpacing: '0.05em'
                  }}>
                    {message.role.toUpperCase()}
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#ffffff',
                    whiteSpace: 'pre-wrap',
                    fontFamily: "'Inter', sans-serif",
                    lineHeight: '1.6'
                  }}>
                    {message.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisModal;







